// NKognito icon system — SVG flags + identity icon avatars (no emoji).
import {
  Compass, Crown, Lightning, Cube, Diamond, Spiral, Triangle, Star,
  Fingerprint, Eye, Atom, Knife, Feather, Sparkle, MoonStars, Flame,
  Globe,
} from "@phosphor-icons/react";

// ---------- Flags ----------
// Hand-built SVGs in a fixed aspect-ratio frame so they look crisp at any size.
export function FlagNG({ size = 16, className = "" }) {
  return (
    <svg width={size} height={size * 0.667} viewBox="0 0 30 20" className={`inline-block rounded-[2px] ${className}`} aria-label="Nigeria">
      <rect x="0" y="0" width="10" height="20" fill="#008751" />
      <rect x="10" y="0" width="10" height="20" fill="#FFFFFF" />
      <rect x="20" y="0" width="10" height="20" fill="#008751" />
    </svg>
  );
}

export function FlagGH({ size = 16, className = "" }) {
  return (
    <svg width={size} height={size * 0.667} viewBox="0 0 30 20" className={`inline-block rounded-[2px] ${className}`} aria-label="Ghana">
      <rect x="0" y="0" width="30" height="6.667" fill="#CE1126" />
      <rect x="0" y="6.667" width="30" height="6.667" fill="#FCD116" />
      <rect x="0" y="13.333" width="30" height="6.667" fill="#006B3F" />
      {/* Black star */}
      <polygon
        points="15,7.5 16.18,11.13 20,11.13 16.91,13.37 18.09,17 15,14.75 11.91,17 13.09,13.37 10,11.13 13.82,11.13"
        fill="#000000"
      />
    </svg>
  );
}

/** FlagOther — NKognito brand mark used on profile / large surfaces for "Other" country players. */
export function FlagOther({ size = 16, className = "" }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-[2px] ${className}`}
      style={{
        width: size,
        height: Math.round(size * 0.667),
        background: "#0f1612",
        border: "1px solid rgba(212,162,76,0.55)",
        color: "#D4A24C",
      }}
      aria-label="Other (Global)"
      data-testid="flag-other"
    >
      <span className="font-display font-bold" style={{ fontSize: Math.round(size * 0.55), lineHeight: 1 }}>N</span>
    </span>
  );
}

/** GlobeInline — small globe used inline next to player names on leaderboards / chat for "Other" players. */
export function GlobeInline({ size = 14, className = "" }) {
  return (
    <Globe
      size={size}
      weight="duotone"
      className={`inline-block shrink-0 ${className}`}
      style={{ color: "#D4A24C" }}
      aria-label="Other (Global)"
      data-testid="globe-other"
    />
  );
}

export function CountryFlag({ country, size = 16, className = "" }) {
  if (!country) return null;
  const code = country.toUpperCase();
  if (code === "NG") return <FlagNG size={size} className={className} />;
  if (code === "GH") return <FlagGH size={size} className={className} />;
  if (code === "ROW" || code === "OT") return <FlagOther size={size} className={className} />;
  // Any other country — flagcdn SVG.
  return (
    <img
      src={`https://flagcdn.com/${code.toLowerCase()}.svg`}
      alt={code}
      width={size}
      height={Math.round(size * 0.667)}
      loading="lazy"
      className={`inline-block rounded-[2px] object-cover ${className}`}
      style={{ width: size, height: Math.round(size * 0.667) }}
      onError={(e) => { e.currentTarget.style.display = "none"; }}
      data-testid={`flag-${code.toLowerCase()}`}
    />
  );
}

/** Inline marker — flag for any country, small globe for ROW. Use inside rows. */
export function CountryInline({ country, size = 14, className = "" }) {
  if (!country) return null;
  const code = country.toUpperCase();
  if (code === "ROW" || code === "OT") return <GlobeInline size={size} className={className} />;
  return <CountryFlag country={code} size={size} className={className} />;
}

export const COUNTRY_NAME = { NG: "Nigeria", GH: "Ghana", ROW: "Rest of World", OT: "Rest of World" };

// ---------- Avatars ----------
// 8 identity tokens — each is an icon + a brand color.
// Backwards-compatible ids ("spy-01"..."spy-08") so existing user docs keep working.
export const AVATARS = [
  { id: "spy-01", label: "Cipher",   Icon: Fingerprint, color: "#10B981", tint: "rgba(16,185,129,0.14)" },
  { id: "spy-02", label: "Phantom",  Icon: MoonStars,   color: "#A78BFA", tint: "rgba(167,139,250,0.14)" },
  { id: "spy-03", label: "Oracle",   Icon: Eye,         color: "#F472B6", tint: "rgba(244,114,182,0.14)" },
  { id: "spy-04", label: "Falcon",   Icon: Triangle,    color: "#F59E0B", tint: "rgba(245,158,11,0.14)" },
  { id: "spy-05", label: "Mongoose", Icon: Flame,       color: "#FB923C", tint: "rgba(251,146,60,0.14)" },
  { id: "spy-06", label: "Sentinel", Icon: Diamond,     color: "#38BDF8", tint: "rgba(56,189,248,0.14)" },
  { id: "spy-07", label: "Echo",     Icon: Spiral,      color: "#34D399", tint: "rgba(52,211,153,0.14)" },
  { id: "spy-08", label: "Quill",    Icon: Feather,     color: "#D4A24C", tint: "rgba(212,162,76,0.14)" },
];

export const getAvatar = (id) => AVATARS.find((a) => a.id === id) || AVATARS[0];

/** Render an avatar token as a square chip with the icon centered. */
export function AvatarIcon({ id, size = 28, className = "" }) {
  const av = getAvatar(id);
  const Icon = av.Icon;
  return (
    <span
      className={`inline-flex items-center justify-center rounded-sm shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        background: av.tint,
        border: `1px solid ${av.color}40`,
        color: av.color,
      }}
      aria-label={av.label}
    >
      <Icon size={Math.round(size * 0.58)} weight="duotone" />
    </span>
  );
}

export default AvatarIcon;
